import * as React from 'react';
import { useEffect, useState } from 'react';
import { Alert, AlertProps, Button, Container, Form } from '@amzn/awsui-components-react-v3';
import {
  DATA_PERMISSION_CONSUMER_OPTION,
  DATA_PERMISSION_STATUS_ACTIVE,
  REDSHIFT_ADHOC,
  TABLE_CONTENT_TYPE,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
} from 'src/commons/constants';
import {
  createRequestForDataPermission,
  getRequestedByRequests,
  getTemplateForResource,
  listDataPermissions,
} from 'src/api/permissions';
import { CreateRequestForDataPermissionRequest } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { RequestContextDetail } from 'src/components/datausageagreement/requestContextDetail';
import { createWorkspaceRequestLink, createWSPermissionLFLink } from 'src/routes';
import { Link } from 'src/components/common/Link';
import { scrollUp } from 'src/components/utils/navigation';
import { getRedshiftMDClustersForAdhocAccess } from 'src/api/config';

export interface BaselineDataAccessPermissionProps {
  setContentType: any;
  match: any;
  username: string;
}

export const BaselineDataAccessPermission = (props: BaselineDataAccessPermissionProps) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertType, setAlertType] = useState('success' as AlertProps.Type);
  const [alertMessage, setAlertMessage] = useState(undefined);
  const [requestContext, setRequestContext] = useState(undefined);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [agreementId, setAgreementId] = useState(undefined);
  const [displayForm, setDisplayForm] = useState(true);
  const [redshiftClustersConfig, setRedshiftClustersConfig] = useState(undefined);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [props.match.params.ownerId]);

  const getExistingRequestsAndPermissions = async (redshiftClustersConfig) => {
    let requestNextToken = null;

    let existingRequests = [];

    do {
      const request = {
        groupId: redshiftClustersConfig.group,
        requestType: 'CreateRequestForDataPermission',
        nextToken: requestNextToken,
      };
      let res = await getRequestedByRequests(request);

      for (let request of res.requestList) {
        if (`midway:${props.username}` == request.consumerPrincipal && 'PENDING' == request.status) {
          let resource = request.dataPermissionRequest?.resource?.redshiftResource;
          if (
            resource != null &&
            resource.awsAccountId == redshiftClustersConfig.account &&
            resource.role == redshiftClustersConfig.role &&
            resource.databaseName == redshiftClustersConfig.database &&
            redshiftClustersConfig.redshiftClusters.includes(resource.clusterId)
          ) {
            existingRequests.push(request.requestId);
          }
        }
      }
      requestNextToken = res.nextToken;
    } while (requestNextToken != null);

    let dataPermissionNextToken = null;
    let existingDataPermissions = [];
    do {
      let listDataPermissionsRequest = {
        ownerId: redshiftClustersConfig.group,
        region: redshiftClustersConfig.region,
        status: DATA_PERMISSION_STATUS_ACTIVE,
        option: DATA_PERMISSION_CONSUMER_OPTION,
        type: REDSHIFT_ADHOC,
        nextToken: dataPermissionNextToken,
      };
      let res = await listDataPermissions(listDataPermissionsRequest);

      for (let dp of res.dataPermissionList) {
        if (
          dp.audit != null &&
          redshiftClustersConfig.redshiftClusters.includes(dp.audit['REDSHIFT_CLUSTER_ID']) &&
          dp.audit['REDSHIFT_GROUP'] == redshiftClustersConfig.group &&
          dp.audit['REDSHIFT_ROLE'] == redshiftClustersConfig.role &&
          dp.audit['REDSHIFT_USER'] == props.username &&
          dp['consumerPrincipal'] == `midway:${props.username}` &&
          dp['ownerId'] == redshiftClustersConfig.group
        ) {
          existingDataPermissions.push(dp.dataPermissionId);
        }
      }
      dataPermissionNextToken = res.nextToken;
    } while (dataPermissionNextToken != null);

    return {
      requestList: existingRequests,
      dataPermissionList: existingDataPermissions,
    };
  };

  const generateMessage = (requestIdList, dataPermissionIdList) => {
    let requests = requestIdList.map((item) => (
      <Link external href={createWorkspaceRequestLink(item)}>
        {item}
      </Link>
    ));
    let dps = dataPermissionIdList.map((item) => (
      <Link external href={createWSPermissionLFLink(item)}>
        {item}
      </Link>
    ));

    if (dataPermissionIdList.length != 0) {
      return <>You already have existing dataPermissions: {dps}</>;
    }

    if (requestIdList.length != 0) {
      return <>You already have existing requests: {requests}</>;
    }

    return '';
  };

  const handleRefresh = async () => {
    try {
      let config = getRedshiftMDClustersForAdhocAccess();
      if (config == null) {
        setDisplayForm(false);
        setAlertVisible(true);
        setAlertMessage('MD Redshift config not found');
        return;
      }
      setRedshiftClustersConfig(config);

      if (!props.match.params.ownerId) return;
      const ownerId = props.match.params.ownerId;
      if (ownerId != config.group) {
        setDisplayForm(false);
        setAlertVisible(true);
        setAlertMessage('This page only handles redshift adhoc access');
        return;
      }

      let existingRequestsAndDps = await getExistingRequestsAndPermissions(config);
      if (
        existingRequestsAndDps['dataPermissionList'].length != 0 ||
        existingRequestsAndDps['requestList'].length != 0
      ) {
        setAlertVisible(true);
        setAlertMessage(
          generateMessage(existingRequestsAndDps['requestList'], existingRequestsAndDps['dataPermissionList']),
        );
        setDisplayForm(false);
      }

      const getARWTemplatesForResourceResponse = await getTemplateForResource({
        resourceId: `DS-redshift|A-${config.account}|CI-${config.redshiftClusters[0]}|DN-${config.database}|R-${config.region}`,
        templateType: TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
      });

      setAgreementId(
        getARWTemplatesForResourceResponse.templates[0].accessRequestWorkflows[0].dataAgreement.agreementId,
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    setButtonLoading(true);
    try {
      let createdRequestIds = [];
      for (let clusterId of redshiftClustersConfig.redshiftClusters) {
        let resourceId = `DS-redshift|A-${redshiftClustersConfig.account}|CI-${clusterId}|DN-${redshiftClustersConfig.database}|R-${redshiftClustersConfig.region}`;

        const getARWTemplatesForResourceResponse = await getTemplateForResource({
          resourceId: resourceId,
          templateType: TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
        });

        let template = getARWTemplatesForResourceResponse.templates[0];

        let createRequestForDataPermissionRequest: CreateRequestForDataPermissionRequest = {
          ownerRequestedBy: redshiftClustersConfig.group,
          ownerRequestedTo: redshiftClustersConfig.group,
          dataConsumerType: 'DataConsumer',
          dataLocationRegion: redshiftClustersConfig.region,
          consumerPrincipal: `midway:${props.username}`,
          resource: {
            redshiftResource: {
              region: redshiftClustersConfig.region,
              clusterId: clusterId,
              role: redshiftClustersConfig.role,
              awsAccountId: redshiftClustersConfig.account,
              databaseName: redshiftClustersConfig.database,
            },
          },
          accessRequestWorkflowMetadata: [
            {
              templateType: 'AccessRequestWorkflow',
              templateId: template.templateId,
              resourceId: resourceId,
              agreementId: template.accessRequestWorkflows[0].dataAgreement.agreementId,
              agreementType: 'DataUsageAgreement',
              requestContextId: requestContext.requestContextId,
            },
          ],
        };
        let response = await createRequestForDataPermission(createRequestForDataPermissionRequest);
        createdRequestIds.push(response.requestId);
      }

      let requestLinks = createdRequestIds.map((item) => (
        <Link external href={createWorkspaceRequestLink(item)}>
          {item}
        </Link>
      ));

      setAlertType('success');
      setAlertMessage(<>Successfully submitted the form, requests created: {requestLinks}</>);
      scrollUp();
      setButtonDisabled(true);
    } catch (error) {
      setAlertType('error');
      setAlertMessage('Failed to submit the form. Please try again later, or contact Omni oncall. Error: ' + error);
      console.error(error);
      setButtonLoading(false);
    } finally {
      setAlertVisible(true);
      setButtonLoading(false);
    }
  };

  return (
    <>
      {alertVisible && (
        <Alert type={alertType} dismissible={false}>
          {alertMessage}
        </Alert>
      )}
      {displayForm && redshiftClustersConfig && (
        <Form
          actions={
            <div>
              <Button
                variant='primary'
                onClick={handleSubmit}
                loading={buttonLoading}
                disabled={!requestContext || buttonDisabled}
              >
                Submit Request
              </Button>
            </div>
          }
        >
          <Container>
            {agreementId && (
              <RequestContextDetail
                {...props}
                displayMetadataContainer={false}
                displaySaveButton={true}
                setContentType={props.setContentType}
                match={props.match}
                activeGroup={redshiftClustersConfig.group}
                dataAgreementId={agreementId}
                requestContext={requestContext}
                setRequestContext={setRequestContext}
                displayPermissionRoles={false}
                omniContext={{}}
                requestContextOwnerOverride={redshiftClustersConfig.group}
              />
            )}
          </Container>
        </Form>
      )}
    </>
  );
};
